// Server-side pagination whose state lives in the URL query: ?page=2&per_page=50.
// Page 1 and the default page size are kept out of the URL.
// A component using this mixin implements fetchItems({ page, perPage }),
// returning a Promise of { items, total, perPage? } (perPage: page size the backend actually applied).
// A table can change its default page size with `defaultPerPage` in its own data().
// Filters are other query params: read them from this.$route.query, change them with updateFilters().

const MAX_PER_PAGE = 100

const toPositiveInt = value => {
  const number = Number(value)
  return Number.isInteger(number) && number > 0 ? number : null
}

const toQueryValue = value => (value === undefined || value === null ? undefined : String(value))

export default {
  data() {
    return {
      items: [],
      total: 0,
      loading: false,
      defaultPerPage: 15,
      responsePerPage: null,
    }
  },
  computed: {
    currentPage() {
      return toPositiveInt(this.$route.query.page) || 1
    },
    perPage() {
      const perPage = toPositiveInt(this.$route.query.per_page)
      return perPage && perPage <= Math.max(MAX_PER_PAGE, this.defaultPerPage) ? perPage : this.defaultPerPage
    },
    numberOfPages() {
      return Math.max(1, Math.ceil(this.total / (this.responsePerPage || this.perPage)))
    },
  },
  watch: {
    $route(to, from) {
      if (to.path === from.path && !this.normalizeQuery()) {
        this.loadItems()
      }
    },
  },
  mounted() {
    if (!this.normalizeQuery()) {
      this.loadItems()
    }
  },
  methods: {
    loadItems() {
      const request = { page: this.currentPage }
      this.activeRequest = request
      this.loading = true

      return this.fetchItems({ page: this.currentPage, perPage: this.perPage })
        .then(result => {
          // A newer request has started (e.g. browser back/forward): drop this response
          if (this.activeRequest !== request) {
            return
          }

          const total = Number(result.total) || 0
          const responsePerPage = toPositiveInt(result.perPage)
          const lastPage = Math.max(1, Math.ceil(total / (responsePerPage || this.perPage)))
          if (request.page > lastPage) {
            // Page is out of range (e.g. after delete): the $route watcher loads the last page
            this.replaceQuery({ page: lastPage > 1 ? lastPage : undefined })
            return
          }

          this.items = result.items
          this.total = total
          this.responsePerPage = responsePerPage
          this.loading = false
        })
        .catch(error => {
          if (this.activeRequest !== request) {
            return
          }
          console.log(error)
          this.loading = false
        })
    },
    // Puts filters into the URL and goes back to the first page; empty values are removed.
    // The $route watcher loads the data. `replace` keeps typing in a search field out of the history.
    updateFilters(filters, { replace = false } = {}) {
      const location = { query: this.buildQuery({ ...filters, page: undefined }) }
      const navigation = replace ? this.$router.replace(location) : this.$router.push(location)
      navigation.catch(() => {})
    },
    // Link generator for b-pagination-nav
    pageLink(page) {
      return { query: this.buildQuery({ page: page > 1 ? page : undefined }) }
    },
    // Replaces invalid or default values in the URL (?page=abc, ?page=1, ?per_page=15).
    // Returns true when the URL was replaced: the $route watcher then loads the data.
    normalizeQuery() {
      const { page, per_page: perPage } = this.$route.query
      const canonicalPage = this.currentPage > 1 ? String(this.currentPage) : undefined
      const canonicalPerPage = this.perPage !== this.defaultPerPage ? String(this.perPage) : undefined

      if (toQueryValue(page) === canonicalPage && toQueryValue(perPage) === canonicalPerPage) {
        return false
      }
      this.replaceQuery({ page: canonicalPage, per_page: canonicalPerPage })
      return true
    },
    buildQuery(changes) {
      const query = { ...this.$route.query, ...changes }
      Object.keys(query).forEach(key => {
        if (query[key] === undefined || query[key] === null || query[key] === '') {
          delete query[key]
        } else if (!Array.isArray(query[key])) {
          query[key] = String(query[key])
        }
      })
      return query
    },
    replaceQuery(changes) {
      this.$router.replace({ query: this.buildQuery(changes) }).catch(() => {})
    },
  },
}
